import { engineComponent } from "neo-common";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Attributes } from "../Utils/Attributes";
import { GeneralConfigurationAttributes } from "../Components/Configuration";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";

/**
 * The symbol element is used for hold symbol special cases and handle symbol behaviour on screen.
 * @class Symbol
 * @extends SwitchElement
 */

@engineComponent(ComponentTypes.DisplayObject)
export class Symbol extends SwitchElement {
    // base symbol cases
    static CaseRotation: string = "ROTATION";
    static CaseBlur: string = "BLUR";

    static CaseEmpty: string = "EMPTY";
    static CaseNormal: string = "NORMAL";
    static CaseIdle: string = "IDLE";
    static CaseNowin: string = "NOWIN";
    static CaseWin: string = "WIN";
    static CaseCycle: string = "CYCLE";
    static CaseSingleWin: string = "SINGLE_WIN";

    //special symbol cases
    static CaseImpact: string = "IMPACT";
    static CaseImpactTease: string = "IMPACT_TEASE";
    static CaseTease: string = "TEASE";
    static CaseImpactWithTease: string = "IMPACT_WITH_TEASE";
    static CaseQuickWin: string = "QUICK_WIN";
    static CaseCycleFS: string = "CYCLE_FS";

    //offsets for symbol which has bigger dimension than standard symbols
    public offsetX: number;
    public offsetY: number;

    // symbol width without offsetX
    private _symbolWidth: number;
    // symbol height without offsetY
    private _symbolHeight: number;

    public zIndex: number = 0;
    public type: string;
    /**
     * @deprecated
     */
    public hasWinFrame: boolean;

    public usedInPool: boolean;
    public id: number;

    protected delayTimer: FrameTimerEventEmitter;

    constructor() {
        super();

        //default values
        this.offsetX = 0;
        this.offsetY = 0;

        this.center = true;

        this.usedInPool = false;
        this.id = -1;

        this.hasWinFrame = true;

        this.delayTimer = CreateFrameTimer("delayTimer");
    }

    public get symbolWidth(): number {
        return this._symbolWidth;
    }

    public get symbolHeight(): number {
        return this._symbolHeight;
    }

    public set symbolWidth(value: number) {
        this._symbolWidth = value;
    }

    public set symbolHeight(value: number) {
        this._symbolHeight = value;
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    createByNode(node: Element): void {
        super.createByNode(node);

        this.offsetX = Attributes.assignFloatAttribute(node, SymbolAttributes.OFFSET_X, this.offsetX);
        this.offsetY = Attributes.assignFloatAttribute(node, SymbolAttributes.OFFSET_Y, this.offsetY);

        if (node.hasAttribute(SymbolAttributes.OFFSET)) {
            this.offsetX = parseFloat(node.getAttribute(SymbolAttributes.OFFSET));
            this.offsetY = parseFloat(node.getAttribute(SymbolAttributes.OFFSET));
        }

        this.zIndex = Attributes.assignFloatAttribute(node, SymbolAttributes.Z_INDEX, this.zIndex);
        this.type = Attributes.assignStringAttribute(node, SymbolAttributes.TYPE, this.type);

        this.hasWinFrame = Attributes.assignBooleanAttribute(node, "HasWinFrame", this.hasWinFrame);

        if (node.hasAttribute(SymbolAttributes.ID)) {
            this.id = parseInt(node.getAttribute(SymbolAttributes.ID));
        }
    }

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    configurate(node: Element): void {
        if (node.hasAttribute(GeneralConfigurationAttributes.SYMBOL_WIDTH)) {
            this.symbolWidth = parseInt(node.getAttribute(GeneralConfigurationAttributes.SYMBOL_WIDTH));
        }

        if (node.hasAttribute(GeneralConfigurationAttributes.SYMBOL_HEIGHT)) {
            this.symbolHeight = parseInt(node.getAttribute(GeneralConfigurationAttributes.SYMBOL_HEIGHT));
        }
    }

    goToCase(s: string, emitEvent: boolean = false, delay?: number, reelIndex?: number): void {
        if (delay > 0) {
            this.delayTimer.stop;
            this.delayTimer.once(FrameTimerEvents.Complete, this.goToCase.bind(this, s, emitEvent, null, reelIndex));
            this.delayTimer.start(delay);
        } else {
            super.goToCase(s, emitEvent);
        }
    }

    stopDelayTimer() {
        this.delayTimer.stop;
        this.delayTimer.removeListener(FrameTimerEvents.Complete);
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return Symbol
     */
    clone(): Symbol {
        let element: Symbol = new Symbol();

        element.elements = this.elements.clone();
        element.addChild(element.elements);

        element.name = this.name;
        element.id = this.id;
        element.style = this.style;
        element.offsetX = this.offsetX;
        element.offsetY = this.offsetY;
        element.symbolWidth = this.symbolWidth;
        element.symbolHeight = this.symbolHeight;
        element.visible = this.visible;
        element.case = this.case;
        element.style = this.style;
        element.zIndex = this.zIndex;
        element.type = this.type;
        element.hasWinFrame = this.hasWinFrame;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;

        element.markAsCloned(); // must be called last before return!
        return element;
    }
}

export const SymbolAttributes = {
    DEBUG: "Debug",
    /**
     *
     */
    ID: "Id",
    /**
     * OffsetX is difference between width of this symbol and standard symbol width divided by two.
     * @property OffsetX
     * @default 0
     */
    OFFSET_X: "OffsetX",
    /**
     * OffsetY is difference between heigth of this symbol and standard symbol height divided by two.
     * @property OffsetX
     * @default 0
     */
    OFFSET_Y: "OffsetY",
    /**
     * Use offset property when offseX and offsetY is the same number.
     * @property Offset
     * @default 0
     */
    OFFSET: "Offset",
    /**
     * Z-index of symbol on screen
     * @property Zindex
     * @default 0
     */
    Z_INDEX: "Zindex",
    /**
     * Type of symbol. E.g.: wild
     */
    TYPE: "Type",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Symbol.ts
 **/